/**
 * The artist's link-in-bio address, with one-click copy.
 *
 * Says which kind of address it is: an artist whose site we host on their own domain gets
 * `links.<their domain>`; everyone else gets `arthaus.bio/<handle>`. The server decides which
 * (lib/easel/bio-link.js) — this only shows it.
 */

import { useEffect, useState } from 'react'
import { Check, Copy } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { toast } from '@/hooks/use-toast'
import type { BioLink } from '../api/easelApi'

const bare = (url: string) => url.replace(/^https?:\/\//, '')

export function BioLinkField({ link }: { link?: BioLink }) {
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    if (!copied) return
    const timer = setTimeout(() => setCopied(false), 2000)
    return () => clearTimeout(timer)
  }, [copied])

  if (!link?.publicUrl) return null

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link.publicUrl)
      setCopied(true)
    } catch {
      toast({
        title: 'Could not copy',
        description: `Select and copy it instead: ${link.publicUrl}`,
        variant: 'destructive',
      })
    }
  }

  const showShared = link.ownDomain && link.sharedUrl && link.sharedUrl !== link.publicUrl

  return (
    <div className="space-y-1">
      <div className="flex flex-wrap items-center gap-2">
        <a
          href={link.publicUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="font-mono text-sm text-foreground underline-offset-4 hover:underline"
        >
          {bare(link.publicUrl)}
        </a>
        <Badge variant={link.ownDomain ? 'default' : 'secondary'}>
          {link.ownDomain ? 'Your domain' : 'Arthaus link'}
        </Badge>
        <Button variant="outline" size="sm" className="h-7 gap-1.5 px-2 text-xs" onClick={copy}>
          {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
          {copied ? 'Copied' : 'Copy link'}
        </Button>
      </div>
      {showShared && (
        <p className="text-xs text-muted-foreground">
          Also works at <span className="font-mono">{bare(link.sharedUrl as string)}</span>
        </p>
      )}
    </div>
  )
}
